import React from 'react';
import { connect } from 'react-redux';

import { MapView } from 'js/pages/BrowsePage/MapView';
import { HybridView } from 'js/pages/BrowsePage/HybridView';
import { BrowsePageOutput } from 'js/model/rainbow/browse-page/BrowsePageOutput';
import * as venuesModule from 'js/redux-modules/browse-page/venues-module';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import {
  LAYOUT_CONFIG_UPDATE,
  LayoutConfigUpdateAction,
} from 'js/redux-modules/layout/actions';
import { windowLocationQueryParameters } from 'js/helpers/uri-util';
import { isPositionError } from 'js/helpers/geo-location';
import { trackFeatureFlagExperiments } from './tracking/tracking-feature-flags';
import { BrowseTitle, CmsPageTitle } from './BrowseTitle';
import { ViewMode } from './ViewModeToggle';
import {
  changeBrowseDataContext,
  ChangeBrowseDataFunction,
  currentGeoLocationAsParameter,
} from './BrowseData';
import { layoutConfig } from './layout-config';

export interface ViewProps {
  browsePageOutput: BrowsePageOutput;
  loading: boolean;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onAnySelectFocus?: () => void;
  hasViewModeChanged?: boolean;
  isInHybridView?: boolean;
  changeBrowseData?: ChangeBrowseDataFunction;
}

interface Props {
  browsePageOutput: BrowsePageOutput;
  loading: boolean;
  cms: CmsBrowsePage & CmsPageTitle;
  featureFlags: string;
}

interface InjectedProps {
  dispatch?: Function;
}

export class BrowsePageInnerComponent extends React.Component<
  Props & InjectedProps
> {
  private onViewModeChange = (mode: ViewMode): void => {
    const changeBrowseData = this.context as ChangeBrowseDataFunction;
    changeBrowseData({}, undefined, undefined, false, mode);

    this.unselectMapVenue();
  };

  private onAnySelectFocus = () => {
    this.unselectMapVenue();
  };

  private unselectMapVenue(): void {
    if (this.props.dispatch) {
      this.props.dispatch(venuesModule.setSelectedMapVenue(null));
    }
  }

  private viewModeChanged = false;

  public async componentDidMount(): Promise<void> {
    this.viewModeChanged = true;
    trackFeatureFlagExperiments(this.props.featureFlags);

    // The presence of a query parameter of 'geolocation' (without
    // regard to any parameter value), will trigger an attempt to
    // use the user's geolocation (if they agree to allow access).
    //
    // The primary use case for this is PPC "near me" campaigns.
    if (windowLocationQueryParameters().geolocation !== undefined) {
      this.changeLocationToCurrentLocation();
    }
  }

  private changeLocationToCurrentLocation = async () => {
    try {
      const currentLocation = await currentGeoLocationAsParameter();
      const changeBrowseData = this.context as ChangeBrowseDataFunction;

      changeBrowseData({
        location: currentLocation,
      });
    } catch (error) {
      // eslint-disable-next-line no-undef
      if (isPositionError(error)) {
        if (error.code !== error.PERMISSION_DENIED) {
          console.warn(error);
        }
      }
    }
  };

  public componentDidUpdate(prevProps: Props): void {
    if (
      this.props.browsePageOutput.viewMode ===
      prevProps.browsePageOutput.viewMode
    ) {
      return;
    }
    this.viewModeChanged = true;

    if (this.props.dispatch) {
      const mode = this.props.browsePageOutput.viewMode;

      this.props.dispatch({
        type: LAYOUT_CONFIG_UPDATE,
        config: layoutConfig(mode),
      } as LayoutConfigUpdateAction);
    }
  }

  private view(changeBrowseData: ChangeBrowseDataFunction): React.ReactElement {
    const viewProps = {
      browsePageOutput: this.props.browsePageOutput,
      loading: this.props.loading,
      viewMode: this.props.browsePageOutput.viewMode || ViewMode.Hybrid,
      onViewModeChange: this.onViewModeChange,
      onAnySelectFocus: this.onAnySelectFocus,
      hasViewModeChanged: this.viewModeChanged,
      changeBrowseData,
    };

    switch (this.props.browsePageOutput.viewMode) {
      case ViewMode.List:
        return <HybridView {...viewProps} listOnly />;
      case ViewMode.Map:
        return <MapView {...viewProps} />;
      default:
        return <HybridView {...viewProps} />;
    }
  }

  public render(): React.ReactNode {
    return (
      <>
        <BrowseTitle
          cms={this.props.cms}
          specification={this.props.browsePageOutput.specification}
          pagination={this.props.browsePageOutput.pagination}
        />

        <changeBrowseDataContext.Consumer>
          {(changeBrowseData) => this.view(changeBrowseData)}
        </changeBrowseDataContext.Consumer>
      </>
    );
  }
}

BrowsePageInnerComponent.contextType = changeBrowseDataContext;

export const BrowsePageInner = connect()(BrowsePageInnerComponent);
